import { useState, useEffect, lazy, Suspense } from "react";
import CitySEOHead from "@/components/feature/CitySEOHead";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const Loading = () => (
  <div className="h-64 flex items-center justify-center bg-[#F7F8FA]">
    <div className="animate-pulse text-gray-400 text-sm">Loading...</div>
  </div>
);

// ─── Data ────────────────────────────────────────────────────────────────────

const allCities = [
  { name: "Seattle", route: "/seattle", tagline: "King County's largest city — Puget Sound gateway for Amazon, Microsoft, Boeing, and Alaska cruise departures", highlights: ["Puget Sound coverage", "Serving Bellevue, Redmond, Kirkland, Renton"], badge: "Most Popular", status: "live" },
  { name: "Bellevue", route: "/bellevue", tagline: "Eastside tech corridor — Microsoft, Amazon HQ2, and the SR-520 bridge to the Seattle agency", highlights: ["Eastside coverage", "Serving Redmond, Kirkland, Issaquah, Sammamish"], badge: "Popular", status: "live" },
  { name: "Tacoma", route: "/tacoma", tagline: "South Sound hub — Port of Tacoma, Joint Base Lewis-McChord, and the Commencement Bay waterfront", highlights: ["Pierce County coverage", "Serving Lakewood, Puyallup, Gig Harbor"], badge: null, status: "live" },
  { name: "Spokane", route: "/spokane", tagline: "Inland Empire gateway — Gonzaga University, healthcare corridor, and the Eastern Washington travel hub", highlights: ["Eastern WA coverage", "Serving Spokane Valley, Coeur d'Alene, Liberty Lake"], badge: null, status: "live" },
  { name: "Spokane Valley", route: "/spokane-valley", tagline: "Eastern Spokane County commercial hub — Liberty Lake tech corridor, Centennial Trail, and the Idaho border gateway", highlights: ["Eastern WA coverage", "Serving Liberty Lake, Otis Orchards, Post Falls"], badge: null, status: "live" },
  { name: "Renton", route: "/renton", tagline: "South King County city adjacent to Seattle — Boeing manufacturing hub and Cedar River trail system", highlights: ["South King County coverage", "Serving Tukwila, Kent, Auburn"], badge: null, status: "live" },
  { name: "Kirkland", route: "/kirkland", tagline: "Eastside city on Lake Washington — Google's Kirkland campus, luxury waterfront community", highlights: ["Eastside coverage", "Serving Redmond, Kenmore, Bothell"], badge: null, status: "live" },
  { name: "Redmond", route: "/redmond-wa", tagline: "Microsoft HQ city — tech campus, Marymoor Park, and the Eastside rail trail corridor", highlights: ["Eastside tech coverage", "Serving Sammamish, Issaquah, Woodinville"], badge: null, status: "live" },
  { name: "Everett", route: "/everett", tagline: "Snohomish County seat — Boeing assembly plant, Paine Field, and the north Puget Sound region", highlights: ["Snohomish County coverage", "Serving Lynnwood, Marysville, Mukilteo"], badge: null, status: "live" },
  { name: "Bellingham", route: "/bellingham", tagline: "Whatcom County city near the Canadian border — Western Washington University and Peace Arch crossing", highlights: ["Northwest WA coverage", "Serving Ferndale, Lynden, Birch Bay"], badge: null, status: "live" },
  { name: "Kennewick", route: "/kennewick", tagline: "Tri-Cities hub — Columbia River wine country and the Hanford nuclear site corridor", highlights: ["Tri-Cities coverage", "Serving Pasco, Richland, Walla Walla"], badge: null, status: "live" },
  { name: "Yakima", route: "/yakima", tagline: "Central Washington agriculture hub — apple orchards, wine country, and Yakima Valley families", highlights: ["Central WA coverage", "Serving Selah, Union Gap, Sunnyside"], badge: null, status: "live" },
  { name: "Marysville", route: "/marysville", tagline: "Snohomish County city near Everett — Tulalip Tribes, outlet shopping, and north Puget Sound communities", highlights: ["North Snohomish coverage", "Serving Tulalip, Smokey Point, Arlington"], badge: null, status: "live" },
  { name: "Federal Way", route: "/federal-way", tagline: "South King County city between Seattle and Tacoma — The Commons mall and Hylebos Wetlands", highlights: ["South King County coverage", "Serving Auburn, Kent, Des Moines"], badge: null, status: "live" },
  { name: "Shoreline", route: "/shoreline", tagline: "North King County city bordering Seattle — Richmond Beach, light rail corridor, and North Shore communities", highlights: ["North Seattle coverage", "Serving Lynnwood, Kenmore, Lake Forest Park"], badge: null, status: "live" },
  { name: "Sammamish", route: "/sammamish", tagline: "Issaquah Plateau community — fastest-growing city in WA, tech families, and lake access", highlights: ["Eastside coverage", "Serving Issaquah, Redmond, Bellevue"], badge: null, status: "live" },
  { name: "Kent", route: "/kent", tagline: "South King County logistics hub — Boeing Field corridor, diverse communities, and the Green River valley", highlights: ["South King County coverage", "Serving Auburn, Renton, Federal Way"], badge: null, status: "live" },
];

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const subregions = [
  {
    id: "puget-sound",
    name: "Puget Sound Metro",
    icon: "ri-anchor-line",
    color: "bg-primary-500",
    description: "The Seattle-Tacoma-Bellevue metro area is the Pacific Northwest's dominant international travel hub. Seattle Passport Agency (915 2nd Ave Suite 500, Seattle WA 98174, Seattle, WA 98104) serves the entire region. SEA Airport offers nonstop flights to Asia, Europe, and everywhere in between.",
    hub: { name: "Seattle", route: "/seattle" },
    communities: [
      { name: "Bellevue", route: "/bellevue" }, { name: "Tacoma", route: "/tacoma" },
      { name: "Renton", route: "/renton" }, { name: "Kirkland", route: "/kirkland" },
      { name: "Redmond", route: "/redmond-wa" }, { name: "Federal Way", route: "/federal-way" },
      { name: "Kent", route: "/kent" }, { name: "Auburn", route: "#" },
      { name: "Shoreline", route: "/shoreline" }, { name: "Burien", route: "#" },
      { name: "Tukwila", route: "#" }, { name: "SeaTac", route: "#" },
      { name: "Mercer Island", route: "#" }, { name: "Sammamish", route: "/sammamish" },
      { name: "Issaquah", route: "#" }, { name: "Bothell", route: "#" },
    ],
  },
  {
    id: "snohomish",
    name: "Snohomish County",
    icon: "ri-flight-takeoff-line",
    color: "bg-accent-500",
    description: "Everett, Lynnwood, Marysville, and Mukilteo sit along I-5 north of Seattle. Paine Field offers commercial flights. The Boeing assembly plant and the growing Snohomish communities make this a significant travel corridor.",
    hub: { name: "Everett", route: "/everett" },
    communities: [
      { name: "Lynnwood", route: "#" }, { name: "Marysville", route: "/marysville" },
      { name: "Mukilteo", route: "#" }, { name: "Mill Creek", route: "#" },
      { name: "Mountlake Terrace", route: "#" }, { name: "Edmonds", route: "#" },
      { name: "Kenmore", route: "#" }, { name: "Bothell", route: "#" },
      { name: "Woodinville", route: "#" }, { name: "Snohomish", route: "#" },
      { name: "Monroe", route: "#" }, { name: "Sultan", route: "#" },
      { name: "Index", route: "#" }, { name: "Granite Falls", route: "#" },
      { name: "Stanwood", route: "#" }, { name: "Arlington", route: "#" },
    ],
  },
  {
    id: "south-sound",
    name: "South Sound (Pierce County)",
    icon: "ri-map-pin-2-line",
    color: "bg-secondary-500",
    description: "Tacoma, Lakewood, Puyallup, Gig Harbor, and the Joint Base Lewis-McChord community anchor the South Sound. Pierce County sits 35 minutes from the Seattle Passport Agency via I-5. Military families and port workers are major passport users in this region.",
    hub: { name: "Tacoma", route: "/tacoma" },
    communities: [
      { name: "Lakewood", route: "#" }, { name: "Puyallup", route: "#" },
      { name: "Gig Harbor", route: "#" }, { name: "University Place", route: "#" },
      { name: "Fife", route: "#" }, { name: "Sumner", route: "#" },
      { name: "Bonney Lake", route: "#" }, { name: "Spanaway", route: "#" },
      { name: "Parkland", route: "#" }, { name: "Steilacoom", route: "#" },
      { name: "DuPont", route: "#" }, { name: "JBLM", route: "#" },
      { name: "Orting", route: "#" }, { name: "Eatonville", route: "#" },
      { name: "Roy", route: "#" }, { name: "Graham", route: "#" },
    ],
  },
  {
    id: "eastern-wa",
    name: "Eastern Washington",
    icon: "ri-landscape-line",
    color: "bg-primary-500",
    description: "Spokane is the Inland Empire's center — 4.5 hours east of Seattle via I-90. Spokane County, the Idaho Panhandle (Coeur d'Alene, Post Falls), and the Palouse all rely on our courier to reach the Seattle Passport Agency across the Cascades.",
    hub: { name: "Spokane", route: "/spokane" },
    communities: [
      { name: "Spokane Valley", route: "/spokane-valley" }, { name: "Cheney", route: "#" },
      { name: "Liberty Lake", route: "#" }, { name: "Airway Heights", route: "#" },
      { name: "Mead", route: "#" }, { name: "Deer Park", route: "#" },
      { name: "Colbert", route: "#" }, { name: "Medical Lake", route: "#" },
      { name: "Coeur d'Alene (ID)", route: "#" }, { name: "Post Falls (ID)", route: "#" },
      { name: "Hayden (ID)", route: "#" }, { name: "Sandpoint (ID)", route: "#" },
      { name: "Moscow (ID)", route: "#" }, { name: "Pullman", route: "#" },
      { name: "Richland", route: "#" }, { name: "Walla Walla", route: "#" },
    ],
  },
];

// ─── SEO Data ────────────────────────────────────────────────────────────────

const washingtonFaqs = [
  { q: "Does Washington have its own passport agency?", a: "Yes — Washington has the Seattle Passport Agency (915 2nd Ave Suite 500, Seattle WA 98174, Seattle, WA 98104). It serves travelers with confirmed travel within 14 days and requires appointments. The agency serves all of Washington State. For Spokane residents 4.5 hours away, Fast Passport Center's courier service is the practical solution." },
  { q: "How fast can I get a passport in Washington?", a: "With Fast Passport Center's courier service, Washington travelers can receive a passport in as little as 24 hours. Our standard expedited service ranges from 3 to 9 business days. Routine processing through the post office typically takes 6 to 13 weeks." },
  { q: "What is Washington State's Alaska cruise passport requirement?", a: "For closed-loop Alaska cruises (departing and returning to Seattle), U.S. citizens can sometimes travel with a government-issued photo ID and birth certificate. However, having a valid passport is strongly recommended — if an emergency requires you to fly home from a Canadian port (Victoria, Juneau, Ketchikan), you cannot board a flight without a passport. Most cruise lines now require passports regardless of itinerary." },
  { q: "Do I need a passport to cross into Canada from Washington?", a: "Yes — a valid passport is required for all air travel to Canada. For land crossings at Peace Arch (Blaine), Lynden, or Sumas, U.S. citizens can use a passport card, enhanced driver's license, or Trusted Traveler card (NEXUS). However, a passport book is recommended for maximum flexibility." },
  { q: "How do Eastern Washington residents get passports quickly?", a: "Eastern Washington (Spokane, Tri-Cities, Yakima) is 4–5 hours from the Seattle Passport Agency. For residents with urgent travel needs, Fast Passport Center's courier service is the fastest option — we hand-carry your application across the Cascades, eliminating the need for you to make the trip." },
  { q: "Can Washington State residents use the Portland Passport Agency?", a: "No — there is no passport agency in Portland, Oregon. Washington residents must use the Seattle Passport Agency. For residents of southwest Washington (Vancouver, Longview), the Seattle agency requires driving north." },
  { q: "What are the busiest times for passport services in Washington?", a: "Alaska cruise season (May–September), Asia-Pacific business travel (year-round due to Boeing, Microsoft, Amazon), and summer family travel (June–August) are the busiest periods. Start your expedited application at least 6 weeks before your departure for best results." },
  { q: "What airport serves Seattle and Washington State?", a: "Seattle-Tacoma International Airport (SEA) is the primary international gateway for Washington State. SEA offers nonstop flights to Asia (Tokyo, Seoul, Beijing, Singapore), Europe, Mexico, Canada, and all major domestic destinations. Paine Field (PAE) in Everett offers limited commercial flights. Spokane International (GEG) serves Eastern Washington." },
];

const washingtonServices = [
  { name: "Emergency Same-Day Service", description: "For Washington travelers with flights within 72 hours. We hand-carry your application to the Seattle Passport Agency — no long drive or appointment stress." },
  { name: "Expedited 2–3 Week Service", description: "Standard expedited processing with overnight shipping and tracking for Washington residents. Perfect for upcoming Alaska cruises, Asia-Pacific business trips, or family travel." },
  { name: "New Passport Applications", description: "First-time applicants in Washington — we handle document prep, photos, and acceptance facility scheduling. Helpful in Eastern Washington and rural communities." },
  { name: "Passport Renewals", description: "Washington renewals by mail or courier. We ensure your application is complete to avoid delays — critical before your next Alaska cruise or Asia business trip." },
  { name: "Alaska Cruise Passports", description: "Washington is the #1 Alaska cruise departure state. We help Seattle and Tacoma cruisers get compliant passports before departure — as fast as 24 hours." },
  { name: "Lost, Stolen & Damaged", description: "Fast replacement for Washington residents who need a new passport quickly — especially before Alaska departures and Asia-Pacific business travel." },
];

// ─── Components ──────────────────────────────────────────────────────────────

function HeroSection() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-white shadow-sm border-b border-gray-100" : "bg-transparent"}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-[70px]">
          <a href="https://fastpassportcenter.com" className="flex items-center">
            <img src="https://staging.fastpassportcenter.com/wp-content/uploads/2026/02/Header.svg" alt="Fast Passport Center" className={`h-9 w-auto transition-all ${scrolled ? "" : "brightness-0 invert"}`} />
          </a>
          <div className="hidden md:flex items-center gap-8">
            {[{ label: "Cities", href: "#cities" }, { label: "Communities", href: "#communities" }, { label: "Resources", href: "#resources" }, { label: "FAQ", href: "#faq" }].map((item) => (
              <a key={item.href} href={item.href} className={`text-sm font-medium transition-colors whitespace-nowrap ${scrolled ? "text-[#1A365D] hover:text-[#F6C344]" : "text-white/90 hover:text-[#F6C344]"}`}>{item.label}</a>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <a href="tel:8772530084" className={`hidden md:inline-flex items-center gap-1.5 text-sm font-semibold transition-colors whitespace-nowrap ${scrolled ? "text-[#1A365D] hover:text-[#F6C344]" : "text-white/90 hover:text-[#F6C344]"}`}>
              <i className="ri-phone-line"></i>877-253-0084
            </a>
            <a href="https://fastpassportcenter.com/checkout/" className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] text-sm font-bold px-5 py-2.5 rounded-md transition-all whitespace-nowrap">Get Started</a>
            <button className="md:hidden flex items-center justify-center w-9 h-9" onClick={() => setMobileOpen(!mobileOpen)}>
              <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl ${scrolled ? "text-[#1A365D]" : "text-white"}`}></i>
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 py-4 space-y-3">
            {[{ label: "Cities", href: "#cities" }, { label: "Communities", href: "#communities" }, { label: "Resources", href: "#resources" }, { label: "FAQ", href: "#faq" }].map((item) => (
              <a key={item.href} href={item.href} onClick={() => setMobileOpen(false)} className="block text-sm font-medium text-[#1A365D] hover:text-[#F6C344] py-1">{item.label}</a>
            ))}
            <a href="tel:8772530084" className="block text-sm font-semibold text-[#1A365D] py-1"><i className="ri-phone-line mr-1.5"></i>877-253-0084</a>
          </div>
        )}
      </nav>

      <section className="relative min-h-[580px] md:min-h-[680px] flex items-center justify-center overflow-hidden bg-[#0F2240]">
        <div className="absolute inset-0 opacity-15">
          <img
            src="https://readdy.ai/api/search-image?query=Stunning%20aerial%20panoramic%20view%20of%20Washington%20State%20with%20the%20Cascade%20Mountains%20and%20Mount%20Rainier%20visible%20in%20the%20distance%2C%20the%20Puget%20Sound%20waters%20reflecting%20dramatic%20Pacific%20Northwest%20skies%2C%20Seattle%20skyline%20at%20golden%20hour%20with%20Space%20Needle%20silhouette%2C%20dense%20evergreen%20forests%20covering%20the%20landscape%2C%20dramatic%20clouds%20over%20the%20mountain%20ranges%2C%20cinematic%20Pacific%20Northwest%20landscape%20photography%20with%20moody%20atmospheric%20lighting%20and%20teal%20and%20amber%20tones&width=1920&height=800&seq=washington-state-hero-001&orientation=landscape"
            alt="Washington State Cascade Mountains and Puget Sound"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F2240]/80 via-[#0F2240]/70 to-[#0F2240]/90"></div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pt-24 pb-16 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-6">
            <i className="ri-map-pin-2-line text-[#F6C344] text-sm"></i>
            <span className="text-white/80 text-sm font-medium">Serving All of Washington State</span>
          </div>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
            Expedited Passport Services<br className="hidden md:block" /> Across Washington State
          </h1>
          <p className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
            From Seattle to Spokane, Tacoma to Bellingham — Fast Passport Center connects Washington travelers with emergency and expedited passport processing — as fast as 24 hours. Seattle Passport Agency is local.
          </p>
          <a href="#cities" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">
            Find Your City <i className="ri-arrow-down-line"></i>
          </a>
          {/* Trust signals */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-5 md:gap-8">
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <div className="flex items-center gap-0.5">
                <i className="ri-star-fill text-[#F6C344] text-sm"></i>
                <i className="ri-star-fill text-[#F6C344] text-sm"></i>
                <i className="ri-star-fill text-[#F6C344] text-sm"></i>
                <i className="ri-star-fill text-[#F6C344] text-sm"></i>
                <i className="ri-star-fill text-[#F6C344] text-sm"></i>
              </div>
              <span className="font-semibold">5.0</span>
              <span>Reviews</span>
            </div>
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <i className="ri-award-line text-[#F6C344] text-base"></i>
              <span>BBB A+</span>
              <span>Accredited</span>
            </div>
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <i className="ri-calendar-check-line text-[#F6C344] text-base"></i>
              <span>20+ Years</span>
            </div>
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <i className="ri-shield-check-line text-[#F6C344] text-base"></i>
              <span>Registered State Dept. Courier</span>
            </div>
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <i className="ri-price-tag-3-line text-[#F6C344] text-base"></i>
              <span>Best Price Guarantee</span>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function CitiesDirectory() {
  const [activeLetter, setActiveLetter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const lettersWithCities = new Set(allCities.map((c) => c.name[0].toUpperCase()));
  const liveCount = allCities.filter((c) => c.status === "live").length;
  const comingCount = allCities.filter((c) => c.status === "coming").length;

  const filteredCities = allCities.filter((c) => {
    if (activeLetter && c.name[0].toUpperCase() !== activeLetter) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      return c.name.toLowerCase().includes(q) || c.tagline.toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <section id="cities" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Washington Cities</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Choose Your Washington Location</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed mb-2">Select your city to view local acceptance facilities, processing options, and expedited services tailored to your area.</p>
          <p className="text-gray-400 text-sm">
            <span className="text-[#1A365D] font-semibold">{liveCount}</span> cities live &nbsp;&middot;&nbsp;
            <span className="text-[#1A365D] font-semibold">{comingCount}</span> more coming soon
          </p>
        </div>

        <div className="max-w-xl mx-auto mb-6">
          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
              <i className="ri-search-line text-gray-400"></i>
            </div>
            <input type="text" value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); if (e.target.value.trim() && activeLetter) setActiveLetter(null); }} placeholder="Search cities..." className="w-full pl-11 pr-10 py-3 bg-white border border-gray-200 rounded-lg text-sm text-[#1A365D] placeholder:text-gray-400 focus:outline-none focus:border-[#F6C344] focus:ring-1 focus:ring-[#F6C344] transition-all" />
            {searchQuery && (
              <button onClick={() => setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600">
                <i className="ri-close-line"></i>
              </button>
            )}
          </div>
        </div>

        <div className="max-w-4xl mx-auto mb-12">
          <div className="flex flex-wrap items-center justify-center gap-1.5 md:gap-2">
            <button onClick={() => setActiveLetter(null)} className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-all whitespace-nowrap ${activeLetter === null ? "bg-[#1A365D] text-white" : "bg-white text-gray-500 hover:bg-[#EEF2F8] hover:text-[#1A365D] border border-gray-200"}`}>All</button>
            {LETTERS.map((letter) => {
              const hasCities = lettersWithCities.has(letter);
              const isActive = activeLetter === letter;
              return (
                <button key={letter} onClick={() => hasCities && setActiveLetter(isActive ? null : letter)} disabled={!hasCities} className={`w-8 h-8 md:w-9 md:h-9 rounded-md text-sm font-semibold flex items-center justify-center transition-all ${isActive ? "bg-[#1A365D] text-white" : hasCities ? "bg-white text-[#1A365D] hover:bg-[#EEF2F8] border border-gray-200 cursor-pointer" : "bg-gray-100 text-gray-300 border border-gray-100 cursor-not-allowed"}`}>
                  {letter}
                </button>
              );
            })}
          </div>
        </div>

        {filteredCities.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCities.map((city, i) =>
              city.status === "live" ? (
                <a key={i} href={city.route} className="group relative bg-white border border-gray-200 rounded-lg p-7 hover:border-[#F6C344] transition-all duration-200 flex flex-col">
                  {city.badge && <span className="absolute top-4 right-4 bg-[#1A365D] text-white text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap">{city.badge}</span>}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0">
                      <i className="ri-map-pin-2-line text-[#1A365D] text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-[#1A365D] text-lg">{city.name}</h3>
                      <p className="text-gray-400 text-xs">Washington</p>
                    </div>
                  </div>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4">{city.tagline}</p>
                  <ul className="space-y-2 mb-6 flex-1">
                    {city.highlights.map((h, j) => (
                      <li key={j} className="flex items-center gap-2 text-sm text-gray-600">
                        <i className="ri-check-line text-[#F6C344] flex-shrink-0"></i>
                        {h}
                      </li>
                    ))}
                  </ul>
                  <div className="inline-flex items-center gap-1.5 text-[#1A365D] font-semibold text-sm group-hover:text-[#F6C344] transition-colors mt-auto whitespace-nowrap">
                    View Services <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform"></i>
                  </div>
                </a>
              ) : (
                <div key={i} className="group relative bg-white border border-dashed border-gray-300 rounded-lg p-7 flex flex-col">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-100 flex-shrink-0">
                      <i className="ri-time-line text-gray-400 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-400 text-lg">{city.name}</h3>
                      <span className="inline-block bg-gray-100 text-gray-500 text-xs font-semibold px-2 py-0.5 rounded-full mt-0.5">Coming Soon</span>
                    </div>
                  </div>
                  <p className="text-gray-400 text-sm leading-relaxed mb-4">{city.tagline}</p>
                  <ul className="space-y-2 mb-6 flex-1">
                    {city.highlights.map((h, j) => (
                      <li key={j} className="flex items-center gap-2 text-sm text-gray-400">
                        <i className="ri-check-line text-gray-300 flex-shrink-0"></i>
                        {h}
                      </li>
                    ))}
                  </ul>
                  <div className="inline-flex items-center gap-1.5 text-gray-400 font-semibold text-sm mt-auto whitespace-nowrap">
                    Coming Soon <i className="ri-time-line"></i>
                  </div>
                </div>
              )
            )}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#EEF2F8] mx-auto mb-4"><i className="ri-search-line text-[#1A365D] text-2xl"></i></div>
            <h3 className="font-bold text-[#1A365D] text-lg mb-2">No cities found</h3>
            <p className="text-gray-500 text-sm max-w-md mx-auto">Try a different search term or letter.</p>
            <button onClick={() => { setActiveLetter(null); setSearchQuery(""); }} className="mt-4 inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-sm transition-colors whitespace-nowrap">Show all cities <i className="ri-arrow-right-line"></i></button>
          </div>
        )}
      </div>
    </section>
  );
}

function CommunitiesWeServe() {
  const [activeSubregion, setActiveSubregion] = useState(subregions[0].id);
  const current = subregions.find((s) => s.id === activeSubregion) || subregions[0];

  return (
    <section id="communities" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Statewide Coverage</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Communities We Serve Across Washington</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">Don't see your city in the directory above? You're still covered. Every Washington community below is served through our nearest hub — with the same fast, reliable expedited passport processing.</p>
        </div>

        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {subregions.map((sub) => (
            <button key={sub.id} onClick={() => setActiveSubregion(sub.id)} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${activeSubregion === sub.id ? "bg-[#1A365D] text-white" : "bg-[#EEF2F8] text-[#1A365D] hover:bg-[#dce5f3]"}`}>
              <i className={`${sub.icon} text-base`}></i>
              {sub.name}
            </button>
          ))}
        </div>

        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-100">
          <div className="flex items-start justify-between gap-4 mb-3">
            <h3 className="font-bold text-[#1A365D] text-base">{current.name}</h3>
            {current.hub.route !== "#" ? (
              <a href={current.hub.route} className="inline-flex items-center gap-1 bg-[#1A365D] text-white text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-[#F6C344] hover:text-[#1A365D] transition-all whitespace-nowrap">
                Hub: {current.hub.name} <i className="ri-arrow-right-line"></i>
              </a>
            ) : (
              <span className="inline-flex items-center gap-1 bg-[#1A365D] text-white text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap">
                Hub: {current.hub.name}
              </span>
            )}
          </div>
          <p className="text-gray-500 text-sm leading-relaxed mb-4">{current.description}</p>
          <div className="flex flex-wrap gap-2">
            {current.communities.map((c, j) => (
              c.route !== "#" ? (
                <a key={j} href={c.route} className="inline-flex items-center text-xs font-medium text-[#1A365D] bg-white border border-gray-200 rounded-full px-3 py-1 hover:border-[#F6C344] transition-all whitespace-nowrap cursor-pointer">{c.name}</a>
              ) : (
                <span key={j} className="inline-flex items-center text-xs font-medium text-[#6B7280] bg-white border border-gray-200 rounded-full px-3 py-1 whitespace-nowrap">{c.name}</span>
              )
            ))}
          </div>
        </div>

        <p className="text-center text-gray-400 text-sm mt-8">
          Not finding your community? <a href="tel:8772530084" className="text-[#1A365D] font-semibold hover:text-[#F6C344] transition-colors">Call us at 877-253-0084</a> — we serve all of Washington State.
        </p>
      </div>
    </section>
  );
}

function StateResources() {
  const airports = [
    { code: "SEA", name: "Seattle-Tacoma International", address: "17801 International Blvd, Seattle, WA 98158", link: "https://www.portseattle.org/sea-tac" },
    { code: "GEG", name: "Spokane International", address: "9000 W Airport Dr, Spokane, WA 99224", link: "https://www.spokaneairports.net/" },
    { code: "PAE", name: "Paine Field — Snohomish County", address: "3220 100th St SW, Everett, WA 98204", link: "https://www.painefield.com/" },
    { code: "BLI", name: "Bellingham International", address: "4255 Mitchell Way, Bellingham, WA 98226", link: "https://www.portofbellingham.com/" },
    { code: "PSC", name: "Tri-Cities Airport", address: "3601 N 20th Ave, Pasco, WA 99301", link: "https://www.flytricities.com/" },
    { code: "YKM", name: "Yakima Air Terminal", address: "2400 W Washington Ave, Yakima, WA 98903", link: "https://www.yakimaairport.com/" },
  ];

  const photoLocations = [
    { name: "CVS Pharmacy", link: "https://www.cvs.com/store-locator/", note: "Walk-in passport photos at most Washington locations" },
    { name: "Walgreens", link: "https://www.walgreens.com/storelocator", note: "Appointment recommended, walk-ins accepted statewide" },
    { name: "Bartell Drugs", link: "https://www.bartelldrugs.com/", note: "Pacific Northwest chain — strong Seattle metro presence" },
    { name: "UPS Store", link: "https://www.theupsstore.com/tools/find-a-store", note: "Also handles overnight shipping of documents" },
    { name: "FedEx Office", link: "https://www.fedex.com/en-us/shipping/store/locator.html", note: "Photos + shipping in one stop" },
    { name: "Costco Pharmacy", link: "https://www.costco.com/photo-center-locations.html", note: "Low-cost passport photos at select Washington locations" },
  ];

  return (
    <section id="resources" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">State Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Washington Passport Resources</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">Everything Washington travelers need — photo locations, airports, the Seattle Passport Agency, and vital records information for the Evergreen State.</p>
        </div>

        {/* Passport Agency */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          <div className="bg-white rounded-lg p-7 border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#EEF2F8]">
                <i className="ri-building-2-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-base">Seattle Passport Agency</h3>
                <p className="text-gray-400 text-xs">For life-or-death emergencies &amp; travel within 14 days</p>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-map-pin-line text-gray-400 text-sm"></i></div>
                <p className="text-gray-600 text-sm">915 2nd Ave Suite 500, Seattle WA 98174<br/>Seattle, WA 98104</p>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-phone-line text-gray-400 text-sm"></i></div>
                <p className="text-gray-600 text-sm">877-487-2778</p>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-time-line text-gray-400 text-sm"></i></div>
                <p className="text-gray-600 text-sm">Mon–Fri 8:00 AM – 3:00 PM (appointment required)</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-gray-500 text-xs leading-relaxed">The Seattle Passport Agency is Washington's regional passport agency, serving the entire state including Eastern Washington. Appointments are required. For Spokane residents (4.5 hours east via I-90), our courier service eliminates the need to cross the Cascades.</p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#EEF2F8]">
                <i className="ri-file-list-3-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-base">Washington Vital Records</h3>
                <p className="text-gray-400 text-xs">Birth certificates for passport applications</p>
              </div>
            </div>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">Washington birth records can be ordered through the Washington State Department of Health — Center for Health Statistics. County auditors also issue certified copies for births in their county.</p>
            <a href="https://www.doh.wa.gov/LicensesPermitsandCertificates/VitalRecords" target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-sm transition-colors whitespace-nowrap">Order Washington Birth Certificate <i className="ri-external-link-line"></i></a>
            <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-time-line text-gray-400 text-sm"></i></div>
                <p className="text-gray-600 text-sm">Standard processing: 3–4 weeks. Expedited processing available for an additional fee.</p>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-money-dollar-circle-line text-gray-400 text-sm"></i></div>
                <p className="text-gray-600 text-sm">$25 per certified copy + county fees (varies by county)</p>
              </div>
            </div>
          </div>
        </div>

        {/* Photo Locations */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#EEF2F8]"><i className="ri-camera-line text-[#1A365D] text-lg"></i></div>
            <div>
              <h3 className="font-bold text-[#1A365D] text-lg">Passport Photo Locations</h3>
              <p className="text-gray-400 text-xs">Find a location near you — most offer walk-in service</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {photoLocations.map((loc, i) => (
              <a key={i} href={loc.link} target="_blank" rel="noopener noreferrer nofollow" className="group bg-white rounded-lg p-5 border border-gray-100 hover:border-[#F6C344]/40 transition-all duration-200">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-[#1A365D] text-sm">{loc.name}</h4>
                  <div className="w-6 h-6 flex items-center justify-center text-gray-400 group-hover:text-[#F6C344] transition-colors"><i className="ri-external-link-line text-sm"></i></div>
                </div>
                <p className="text-gray-500 text-xs leading-relaxed">{loc.note}</p>
              </a>
            ))}
          </div>
        </div>

        {/* Airports */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#EEF2F8]"><i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i></div>
            <div>
              <h3 className="font-bold text-[#1A365D] text-lg">Major Washington Airports</h3>
              <p className="text-gray-400 text-xs">International departure points — verify passport validity before booking</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {airports.map((ap, i) => (
              <a key={i} href={ap.link} target="_blank" rel="noopener noreferrer nofollow" className="group bg-white rounded-lg p-5 border border-gray-100 hover:border-[#F6C344]/40 transition-all duration-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="bg-[#1A365D] text-white text-xs font-bold px-2 py-0.5 rounded">{ap.code}</span>
                    <h4 className="font-semibold text-[#1A365D] text-sm">{ap.name}</h4>
                  </div>
                  <div className="w-6 h-6 flex items-center justify-center text-gray-400 group-hover:text-[#F6C344] transition-colors"><i className="ri-external-link-line text-sm"></i></div>
                </div>
                <p className="text-gray-500 text-xs leading-relaxed">{ap.address}</p>
              </a>
            ))}
          </div>
        </div>

        <div className="bg-[#0F2240] rounded-lg p-6 md:p-8 text-center">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/10 mx-auto mb-4">
            <i className="ri-lightbulb-line text-[#F6C344] text-xl"></i>
          </div>
          <h4 className="font-bold text-white text-base mb-2">Pro Tip for Washington Travelers</h4>
          <p className="text-white/70 text-sm max-w-2xl mx-auto leading-relaxed">
            Washington is unique in its geography — the Cascades divide East and West. Seattle residents enjoy a local passport agency, while Spokane residents face a 4.5-hour drive. Alaska cruise season (May–September) is the #1 passport rush period — Seattle is the top Alaska cruise departure port in the US. Asia-Pacific business travel from Boeing, Microsoft, and Amazon is year-round. If your trip falls during peak windows, start your expedited application at least 4–6 weeks early. Our courier service eliminates the need to travel to the agency regardless of where you are in Washington.
          </p>
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  return (
    <section id="faq" className="bg-white py-20 md:py-28">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">FAQ</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Washington Passport Questions</h2>
          <p className="text-gray-500 text-base leading-relaxed">Quick answers for travelers applying for passports across the Evergreen State.</p>
        </div>
        <div className="space-y-4">
          {washingtonFaqs.map((faq, i) => (
            <details key={i} className="group bg-[#F7F8FA] rounded-lg border border-gray-200 overflow-hidden">
              <summary className="flex items-center justify-between cursor-pointer p-5 list-none">
                <span className="font-bold text-[#1A365D] text-sm pr-4">{faq.q}</span>
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-white flex-shrink-0"><i className="ri-add-line text-[#1A365D] text-sm group-open:hidden"></i><i className="ri-subtract-line text-[#1A365D] text-sm hidden group-open:block"></i></div>
              </summary>
              <div className="px-5 pb-5"><p className="text-gray-500 text-sm leading-relaxed">{faq.a}</p></div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="bg-[#0F2240] py-20 md:py-28">
      <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
        <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-5">Ready to Get Your Passport Fast?</h2>
        <p className="text-white/70 text-lg max-w-xl mx-auto mb-10 leading-relaxed">Whether you are in Seattle, Bellevue, Tacoma, Spokane, or anywhere in between — we help Washington travelers get their passports on time. Start your application in minutes.</p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">
            Start My Passport <i className="ri-arrow-right-line"></i>
          </a>
          <a href="tel:8772530084" className="inline-flex items-center gap-2 border border-white/30 text-white font-semibold text-base px-8 py-4 rounded-md transition-all hover:bg-white/10 whitespace-nowrap">
            <i className="ri-phone-line"></i>877-253-0084
          </a>
        </div>
        <p className="text-white/40 text-sm mt-8">A+ BBB Rated &bull; Registered U.S. Department of State Courier &bull; 20+ Years of Service</p>
      </div>
    </section>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function WashingtonPage() {
  return (
    <div className="min-h-screen bg-white">
      <CitySEOHead
        city="Washington"
        state="Washington"
        stateAbbr="WA"
        route="/states/washington"
        title="Expedited & Emergency Passport Services Washington State | Fast Passport Center"
        description="Fast Passport Center provides emergency, expedited, and same-day passport services across all Washington State cities. Serving Seattle, Bellevue, Tacoma, Spokane, and beyond. Seattle Passport Agency is local. Courier service covers all of Washington."
        geoPlacename="Washington"
        geoPosition="47.7511;-120.7401"
        faqs={washingtonFaqs}
        servicesSchema={washingtonServices}
        keywords="expedited passport Washington, emergency passport Washington, same day passport WA, fast passport Seattle, passport renewal Bellevue, passport service Spokane"
      />
      <HeroSection />
      <CitiesDirectory />
      <CommunitiesWeServe />
      <StateResources />

      <section className="bg-[#F7F8FA] pt-8 md:pt-10 pb-20 md:pb-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Passport Services</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How We Serve Washington</h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">From the tech corridors of Seattle and Bellevue to the waterfront of Tacoma, from the Inland Empire of Spokane to the agricultural heartland of Yakima — we help Washington travelers get their passports fast. One regional agency in Seattle serves the entire state.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: "ri-flight-takeoff-line", title: "Emergency Same-Day Service", desc: "For Washington travelers with flights within 72 hours. We hand-carry your application to the Seattle Passport Agency — no long drive or appointment stress." },
              { icon: "ri-time-line", title: "Expedited 2–3 Week Service", desc: "Standard expedited processing with overnight shipping and tracking for Washington residents. Perfect for upcoming Alaska cruises, Asia-Pacific business trips, or family travel." },
              { icon: "ri-file-list-3-line", title: "New Passport Applications", desc: "First-time applicants in Washington — we handle document prep, photos, and acceptance facility scheduling. Helpful in Eastern Washington and rural communities." },
              { icon: "ri-refresh-line", title: "Passport Renewals", desc: "Washington renewals by mail or courier. We ensure your application is complete to avoid delays — critical before your next Alaska cruise or Asia business trip." },
              { icon: "ri-anchor-line", title: "Alaska Cruise Passports", desc: "Washington is the #1 Alaska cruise departure state. We help Seattle and Tacoma cruisers get compliant passports before departure — as fast as 24 hours." },
              { icon: "ri-error-warning-line", title: "Lost, Stolen & Damaged", desc: "Fast replacement for Washington residents who need a new passport quickly — especially before Alaska departures and Asia-Pacific business travel." },
            ].map((svc, i) => (
              <div key={i} className="bg-white rounded-lg p-7 border border-gray-100 hover:border-[#F6C344]/40 transition-all duration-200">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-5"><i className={`${svc.icon} text-[#1A365D] text-xl`}></i></div>
                <h3 className="font-bold text-[#1A365D] text-base mb-2">{svc.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{svc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FAQSection />
      <FinalCTA />

      <section className="bg-[#F7F8FA] py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <SEOCrossLinks state="WA" showCities={false} />
        </div>
      </section>
    </div>
  );
}